type RGB = [number, number, number];

/** DIG's base text color on each theme's base background. */
export const BANNER_TEXT_COLORS = {bright: '#1A1918', dark: '#FFFFFF'} as const;

/** WCAG AA for normal text. */
export const MIN_BANNER_CONTRAST = 4.5;

/** Used when a banner has no light-mode color yet. */
export const DEFAULT_LIGHT_BANNER_COLOR = '#F7F5F2';

const HEX = /^#[0-9a-f]{6}$/i;

export const isHexColor = (value: string) => HEX.test(value);

export const hexToRgb = (hex: string): RGB => {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const clampChannel = (channel: number) => Math.min(255, Math.max(0, Math.round(channel)));

export const rgbToHex = ([r, g, b]: RGB) =>
  `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** WCAG relative luminance, 0 (black) to 1 (white). */
const relativeLuminance = (hex: string) => {
  const [r, g, b] = hexToRgb(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a: string, b: string) => {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Whether the theme's text is readable on `background`. */
export const hasReadableText = (background: string, mode: 'bright' | 'dark') =>
  contrastRatio(background, BANNER_TEXT_COLORS[mode]) >= MIN_BANNER_CONTRAST;

const rgbToHsl = ([r, g, b]: RGB): [number, number, number] => {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const lightness = (max + min) / 2;
  if (max === min) {
    return [0, 0, lightness];
  }
  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  const hue =
    max === rn
      ? ((gn - bn) / delta + (gn < bn ? 6 : 0)) / 6
      : max === gn
        ? ((bn - rn) / delta + 2) / 6
        : ((rn - gn) / delta + 4) / 6;
  return [hue, saturation, lightness];
};

const hslToRgb = ([h, s, l]: [number, number, number]): RGB => {
  if (s === 0) {
    return [l * 255, l * 255, l * 255];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    const tn = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tn < 1 / 6) return p + (q - p) * 6 * tn;
    if (tn < 1 / 2) return q;
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
    return p;
  };
  return [channel(h + 1 / 3) * 255, channel(h) * 255, channel(h - 1 / 3) * 255];
};

/** The hue of a color as 0-1, for checking that a suggestion kept it. */
export const hueOf = (hex: string) => rgbToHsl(hexToRgb(hex))[0];

// Below this spread between channels a color reads as gray. Its HSL saturation
// can still be high (near black or white, tiny differences divide by a tiny
// range), which would turn an off-white backdrop into a strong brown.
const MIN_CHROMA = 0.08;

/** Hue, saturation, and lightness, with near-grays treated as gray. */
const toneOf = (hex: string): [number, number, number] => {
  const rgb = hexToRgb(hex);
  const [h, s, l] = rgbToHsl(rgb);
  const chroma = (Math.max(...rgb) - Math.min(...rgb)) / 255;
  return [h, chroma < MIN_CHROMA ? 0 : s, l];
};

/**
 * A light-mode banner color from an image color: the same hue, lightened just
 * enough for dark text to stay readable.
 */
export const suggestLightColor = (hex: string) => {
  const [h, s, l] = toneOf(hex);
  for (let lightness = Math.max(l, 0.5); lightness <= 1; lightness += 0.01) {
    const candidate = rgbToHex(hslToRgb([h, s, lightness]));
    if (hasReadableText(candidate, 'bright')) {
      return candidate;
    }
  }
  return '#FFFFFF';
};

/**
 * A dark-mode banner color from an image color: the same hue, toned down and
 * darkened until white text is readable, so it stays recognizably the same
 * color without glowing on a dark page.
 */
export const suggestDarkColor = (hex: string) => {
  const [h, s, l] = toneOf(hex);
  const saturation = Math.min(s, 0.55);
  for (let lightness = Math.min(l, 0.35); lightness >= 0; lightness -= 0.01) {
    const candidate = rgbToHex(hslToRgb([h, saturation, lightness]));
    if (hasReadableText(candidate, 'dark')) {
      return candidate;
    }
  }
  return BANNER_TEXT_COLORS.bright;
};

/** Same solid-then-fade shape as the calendar-driven banners. */
export const bannerGradient = (hex: string) =>
  `linear-gradient(90deg, ${hex} 44.09%, ${hex}00 100%)`;
